/**
 * Analytics Service for Amplitude
 * Centralized tracking for all user interactions and funnel events
 */

// Event names - use consistent naming convention
export const EVENTS = {
  // Page views
  PAGE_VIEW: 'Page Viewed',

  // Funnel steps
  FUNNEL_DIMENSIONS_ENTERED: 'Funnel: Dimensions Entered',
  FUNNEL_COLOR_SELECTED: 'Funnel: Color Selected',
  FUNNEL_IMAGE_UPLOADED: 'Funnel: Image Uploaded',
  FUNNEL_DELIVERY_SELECTED: 'Funnel: Delivery Selected',
  FUNNEL_ORDER_STARTED: 'Funnel: Order Started',
  FUNNEL_ORDER_COMPLETED: 'Funnel: Order Completed',

  // Calculator interactions
  CALCULATOR_DIMENSION_CHANGED: 'Calculator: Dimension Changed',
  CALCULATOR_FINISH_CHANGED: 'Calculator: Finish Changed',
  CALCULATOR_COLOR_MODE_CHANGED: 'Calculator: Color Mode Changed',
  CALCULATOR_COLOR_SELECTED: 'Calculator: Color Selected',
  CALCULATOR_RAL_SEARCHED: 'Calculator: RAL Searched',
  CALCULATOR_BACKGROUND_TONE_CHANGED: 'Calculator: Background Tone Changed',

  // Image interactions
  IMAGE_UPLOAD_STARTED: 'Image: Upload Started',
  IMAGE_UPLOAD_COMPLETED: 'Image: Upload Completed',
  IMAGE_UPLOAD_FAILED: 'Image: Upload Failed',
  IMAGE_REMOVED: 'Image: Removed',

  // Delivery interactions
  DELIVERY_COUNTRY_CHANGED: 'Delivery: Country Changed',
  DELIVERY_CARRIER_CHANGED: 'Delivery: Carrier Changed',
  DELIVERY_POSTAL_CODE_ENTERED: 'Delivery: Postal Code Entered',

  // Section interactions
  SECTION_EXPANDED: 'Section: Expanded',
  SECTION_COLLAPSED: 'Section: Collapsed',

  // Order interactions
  ORDER_BUTTON_CLICKED: 'Order: Button Clicked',
  ORDER_MODAL_OPENED: 'Order: Modal Opened',
  ORDER_MODAL_CLOSED: 'Order: Modal Closed',
  ORDER_FORM_SUBMITTED: 'Order: Form Submitted',
  ORDER_PAYMENT_STARTED: 'Order: Payment Started',
  ORDER_COMPLETED: 'Order: Completed',
  ORDER_FAILED: 'Order: Failed',

  // Navigation
  NAV_LINK_CLICKED: 'Navigation: Link Clicked',
  LANGUAGE_CHANGED: 'Language: Changed',

  // Gallery
  GALLERY_IMAGE_VIEWED: 'Gallery: Image Viewed',
  GALLERY_IMAGE_CLICKED: 'Gallery: Image Clicked',

  // Errors
  ERROR_OCCURRED: 'Error: Occurred',
  VALIDATION_ERROR: 'Validation: Error'
};

// User properties to track
export const USER_PROPERTIES = {
  LANGUAGE: 'language',
  COUNTRY: 'country',
  TOTAL_ORDERS: 'total_orders',
  FIRST_VISIT_DATE: 'first_visit_date',
  LAST_VISIT_DATE: 'last_visit_date'
};

// Funnel steps for tracking progression
export const FUNNEL_STEPS = {
  DIMENSIONS: 1,
  COLOR: 2,
  IMAGE: 3,
  DELIVERY: 4,
  ORDER_STARTED: 5,
  ORDER_COMPLETED: 6
};

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.amplitude = null;
    this.debugMode = process.env.NODE_ENV === 'development';
    this.eventQueue = [];
    this.funnelProgress = {};
  }

  /**
   * Initialize Amplitude SDK
   * @param {string} apiKey - Amplitude API key
   */
  async init(apiKey) {
    if (this.isInitialized) return;

    try {
      // Dynamic import of Amplitude
      const amplitudeModule = await import('@amplitude/analytics-browser');
      this.amplitude = amplitudeModule;

      this.amplitude.init(apiKey, undefined, {
        defaultTracking: {
          sessions: true,
          pageViews: false, // We'll track these manually for more control
          formInteractions: false,
          fileDownloads: false
        }
      });

      this.isInitialized = true;

      // Process queued events
      this.eventQueue.forEach(({ event, properties }) => {
        this.track(event, properties);
      });
      this.eventQueue = [];

      if (this.debugMode) {
        console.log('[Analytics] Initialized successfully');
      }
    } catch (error) {
      console.error('[Analytics] Failed to initialize:', error);
    }
  }

  /**
   * Track an event
   * @param {string} eventName - Name of the event
   * @param {object} properties - Event properties
   */
  track(eventName, properties = {}) {
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname
    };

    if (this.debugMode) {
      console.log('[Analytics] Track:', eventName, enrichedProperties);
    }

    if (!this.isInitialized) {
      this.eventQueue.push({ event: eventName, properties: enrichedProperties });
      return;
    }

    try {
      this.amplitude.track(eventName, enrichedProperties);
    } catch (error) {
      console.error('[Analytics] Track error:', error);
    }
  }

  /**
   * Track page view
   * @param {string} pageName - Name of the page
   * @param {object} properties - Additional properties
   */
  trackPageView(pageName, properties = {}) {
    this.track(EVENTS.PAGE_VIEW, {
      page_name: pageName,
      referrer: document.referrer,
      ...properties
    });
  }

  /**
   * Track funnel step
   * @param {number} step - Funnel step number
   * @param {string} stepName - Name of the step
   * @param {object} properties - Additional properties
   */
  trackFunnelStep(step, stepName, properties = {}) {
    // Only track if this is a new step or progression
    const sessionId = this.getSessionId();
    const currentStep = this.funnelProgress[sessionId] || 0;

    if (step > currentStep) {
      this.funnelProgress[sessionId] = step;
      this.track(stepName, {
        funnel_step: step,
        funnel_name: 'Frame Order',
        ...properties
      });
    }
  }

  /**
   * Set user properties
   * @param {object} properties - User properties to set
   */
  setUserProperties(properties) {
    if (this.debugMode) {
      console.log('[Analytics] Set User Properties:', properties);
    }

    if (!this.isInitialized) return;

    try {
      const identify = new this.amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value);
      });
      this.amplitude.identify(identify);
    } catch (error) {
      console.error('[Analytics] Set user properties error:', error);
    }
  }

  /**
   * Set user ID
   * @param {string} userId - User identifier
   */
  setUserId(userId) {
    if (this.debugMode) {
      console.log('[Analytics] Set User ID:', userId);
    }

    if (!this.isInitialized) return;

    try {
      this.amplitude.setUserId(userId);
    } catch (error) {
      console.error('[Analytics] Set user ID error:', error);
    }
  }

  /**
   * Get or create session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Track calculator dimension change
   */
  trackDimensionChange(width, height, isValid) {
    this.track(EVENTS.CALCULATOR_DIMENSION_CHANGED, {
      width,
      height,
      is_valid: isValid,
      area_cm2: isValid ? width * height : null
    });

    if (isValid && width && height) {
      this.trackFunnelStep(FUNNEL_STEPS.DIMENSIONS, EVENTS.FUNNEL_DIMENSIONS_ENTERED, {
        width,
        height
      });
    }
  }

  /**
   * Track color selection
   */
  trackColorSelected(colorMode, colorName, colorValue) {
    this.track(EVENTS.CALCULATOR_COLOR_SELECTED, {
      color_mode: colorMode,
      color_name: colorName,
      color_value: colorValue
    });

    this.trackFunnelStep(FUNNEL_STEPS.COLOR, EVENTS.FUNNEL_COLOR_SELECTED, {
      color_mode: colorMode,
      color_name: colorName
    });
  }

  /**
   * Track image upload
   */
  trackImageUpload(status, fileSize = null, fileType = null) {
    const eventName = status === 'completed'
      ? EVENTS.IMAGE_UPLOAD_COMPLETED
      : status === 'failed'
        ? EVENTS.IMAGE_UPLOAD_FAILED
        : EVENTS.IMAGE_UPLOAD_STARTED;

    this.track(eventName, {
      file_size: fileSize,
      file_type: fileType
    });

    if (status === 'completed') {
      this.trackFunnelStep(FUNNEL_STEPS.IMAGE, EVENTS.FUNNEL_IMAGE_UPLOADED);
    }
  }

  /**
   * Track delivery selection
   */
  trackDeliverySelection(country, carrier, shippingCost) {
    this.track(EVENTS.DELIVERY_CARRIER_CHANGED, {
      country,
      carrier,
      shipping_cost: shippingCost
    });

    this.trackFunnelStep(FUNNEL_STEPS.DELIVERY, EVENTS.FUNNEL_DELIVERY_SELECTED, {
      country,
      carrier
    });
  }

  /**
   * Track order events
   */
  trackOrderEvent(eventType, orderData = {}) {
    const eventMap = {
      'button_clicked': EVENTS.ORDER_BUTTON_CLICKED,
      'modal_opened': EVENTS.ORDER_MODAL_OPENED,
      'modal_closed': EVENTS.ORDER_MODAL_CLOSED,
      'form_submitted': EVENTS.ORDER_FORM_SUBMITTED,
      'payment_started': EVENTS.ORDER_PAYMENT_STARTED,
      'completed': EVENTS.ORDER_COMPLETED,
      'failed': EVENTS.ORDER_FAILED
    };

    const eventName = eventMap[eventType] || EVENTS.ORDER_BUTTON_CLICKED;
    this.track(eventName, orderData);

    if (eventType === 'button_clicked') {
      this.trackFunnelStep(FUNNEL_STEPS.ORDER_STARTED, EVENTS.FUNNEL_ORDER_STARTED, orderData);
    } else if (eventType === 'completed') {
      this.trackFunnelStep(FUNNEL_STEPS.ORDER_COMPLETED, EVENTS.FUNNEL_ORDER_COMPLETED, orderData);
    }
  }

  /**
   * Track section expand/collapse
   */
  trackSectionToggle(sectionName, isExpanded) {
    this.track(isExpanded ? EVENTS.SECTION_EXPANDED : EVENTS.SECTION_COLLAPSED, {
      section_name: sectionName
    });
  }

  /**
   * Track errors
   */
  trackError(errorType, errorMessage, context = {}) {
    this.track(EVENTS.ERROR_OCCURRED, {
      error_type: errorType,
      error_message: errorMessage,
      ...context
    });
  }
}

// Export singleton instance
const analytics = new AnalyticsService();
export default analytics;
